import React from 'react';
import styled from 'styled-components';
import { DropdownWithPlaceholder, DropdownItem } from '../Dropdown';
import type { SelectChangeEvent } from '@mui/material';
import { IconButton } from 'design-system/src/components/Button/Button';
import Close from 'design-system/src/assets/icons/close.svg?react';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { Tooltip } from 'design-system/src/components/Tooltip/Tooltip';

const StyledInteractiveListItem = styled.div`
  display: flex;
  padding: 4px 4px 4px 8px;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  border-radius: var(--border-radius-medium);
  background: var(--grayscale-50);
  min-height: 44px;
`;

const ContentArea = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  gap: 8px;
`;

const DropdownContainer = styled.div<{ $width?: string }>`
  width: ${({ $width = '120px' }) => $width};
  padding-right: 8px;

  & > * {
    background-color: var(--white);
    height: 36px;
    width: 100% !important;
  }
`;

const StyledInteractiveList = styled.div<{ $height?: string }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  height: ${({ $height }) => $height || '200px'};
`;

export interface DropdownProps {
  value: string;
  onChange: (event: SelectChangeEvent<unknown>) => void;
  options: Array<{
    value: string;
    label: string;
    disabled?: boolean;
  }>;
  width?: string;
  disabled?: boolean;
}

export interface RemoveButtonProps {
  onClick: () => void;
  title: string;
  disabled?: boolean;
  disabledTooltip?: string;
}

interface InteractiveListItemProps {
  /**
   * Content to display in the main content area
   */
  children: React.ReactNode;
  /**
   * Optional remove button/icon to display on the left
   */
  removeButtonProps?: RemoveButtonProps;
  /**
   * Optional dropdown to display on the right
   */
  dropdown?: React.ReactNode;
  className?: string;
  /**
   * ARIA role for the list item
   */
  role?: string;
}

/**
 * Interactive list item component for displaying items in a list with consistent styling.
 *
 * Layout: [showRemoveButton] [children] [rightAction|dropdown]
 * - showRemoveButton: Optional element on the left (typically an X or remove icon)
 * - children: Main content in the center (flexes to take available space)
 * - dropdown: Optional dropdown configuration that creates a dropdown select
 */
export const InteractiveListItem: React.FC<InteractiveListItemProps> = ({
  children,
  removeButtonProps,
  dropdown,
  className,
  role,
}) => {
  return (
    <StyledInteractiveListItem className={className} role={role}>
      {removeButtonProps && (
        <Tooltip
          content={
            removeButtonProps.disabled && removeButtonProps.disabledTooltip
              ? removeButtonProps.disabledTooltip
              : 'Remove'
          }
          placement="bottom"
        >
          <span>
            <IconButton onClick={removeButtonProps.onClick} size="sm" disabled={removeButtonProps.disabled}>
              <Close title={removeButtonProps.title} />
            </IconButton>
          </span>
        </Tooltip>
      )}
      <ContentArea>{children}</ContentArea>
      {dropdown}
    </StyledInteractiveListItem>
  );
};

export interface InteractiveListItem {
  id: string;
  content: React.ReactNode;
  dropdownValue?: string;
  isInteractionDisabled?: boolean;
  disabledTooltip?: string;
}

export interface InteractiveListProps<T extends InteractiveListItem> {
  items: T[];
  onRemoveItem: (id: string) => void;
  onDropdownChange?: (id: string, value: string) => void;
  dropdownOptions?: Array<{
    value: string;
    label: string;
    disabled?: boolean;
  }>;
  dropdownWidth?: string;
  emptyStateMessage?: string;
  className?: string;
  height?: string;
}

const EmptyStateMessageContainer = styled(Typography)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

export const ListItemDropdown = ({ value, onChange, options, width, disabled }: DropdownProps) => {
  return (
    <DropdownContainer $width={width}>
      <DropdownWithPlaceholder value={value} onChange={onChange} disabled={disabled}>
        {options.map((option) => (
          <DropdownItem key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </DropdownItem>
        ))}
      </DropdownWithPlaceholder>
    </DropdownContainer>
  );
};

export const InteractiveList = <T extends InteractiveListItem>({
  items,
  onRemoveItem,
  onDropdownChange,
  dropdownOptions,
  height,
  dropdownWidth = '120px',
  emptyStateMessage = 'No items added yet.',
  className,
}: InteractiveListProps<T>) => {
  if (items.length === 0) {
    return (
      <StyledInteractiveList className={className} $height={height}>
        <EmptyStateMessageContainer>{emptyStateMessage}</EmptyStateMessageContainer>
      </StyledInteractiveList>
    );
  }

  return (
    <StyledInteractiveList className={className} $height={height} role="table">
      {items.map((item) => (
        <InteractiveListItem
          key={item.id}
          role="row"
          removeButtonProps={{
            onClick: () => onRemoveItem(item.id),
            title: 'Remove item',
            disabled: item.isInteractionDisabled,
            disabledTooltip: item.disabledTooltip,
          }}
          dropdown={
            dropdownOptions && onDropdownChange ? (
              <ListItemDropdown
                value={item.dropdownValue || ''}
                onChange={(e) => onDropdownChange(item.id, (e.target as HTMLSelectElement).value)}
                options={dropdownOptions}
                width={dropdownWidth}
                disabled={item.isInteractionDisabled}
              />
            ) : undefined
          }
        >
          {item.content}
        </InteractiveListItem>
      ))}
    </StyledInteractiveList>
  );
};
